import React, { useCallback, useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { Fields, Columns } from 'modules/af_cm_ui_kit/components';
import { VARIANTS_TYPES } from 'modules/af_cm_ui_kit/components/TextField/variants.types';
import { FIELDS, FIELDS_QUESTIONS } from '../Form/Form.types';
import { Props } from './LoanDetails.types';
import { ProductRepaymentType, ProductTypes, ProductRepaymentTypes } from 'domain/Product';
import { Nullable } from '../../../../../../utils/types';
import get from 'get-value';
import { ukPostalCode } from '../../../../../../shared/regex/regex';
import { checkMinimumRemainingEquity, checkPostcode } from '../Form/utils/businessRulesValidations';
import { MinimumRemainingEquityValidationError } from '../../../../../business_rules/api/minimumRemainingEquity';
import { mapRepaymentTypes } from './mapper';
import { Option } from 'shared/interface/globalInterface';
import { yesNoOptions } from 'utils/form/formUtils';

const LoanDetails: React.FC<Props> = ({
  products = [],
  termLengths = [],
  productType,
  handleProductChange,
  values,
}) => {
  const getName = (field: FIELDS): string => {
    return `${FIELDS.LOAN_DETAILS_PREFIX}.${field}`;
  };
  const { setFieldValue } = useFormikContext<any>();

  const [loanAmountError, setLoanAmountError] = useState<Nullable<string>>();
  const [availableRepaymentTypes, setAvailableRepaymentTypes] = useState<
    { option: any; value: ProductRepaymentType }[]
  >([]);

  const validateMRE = useCallback(async (): Promise<Nullable<string>> => {
    if (
      productType !== ProductTypes.TIO ||
      get(values, getName(FIELDS.PRODUCT_REPAYMENT_TYPE)) !== ProductRepaymentTypes.INTEREST_ONLY
    ) {
      return null;
    }

    const postcode = get(values, getName(FIELDS.POST_CODE));
    const loanAmountRequired = get(values, getName(FIELDS.LOAN_REQUESTED));
    const propertyValue = get(values, getName(FIELDS.PROPERTY_VALUE));

    if (loanAmountRequired && propertyValue && ukPostalCode.test(postcode)) {
      try {
        await checkPostcode(postcode);
        await checkMinimumRemainingEquity(loanAmountRequired, Number(propertyValue), postcode);
      } catch (error) {
        if (error instanceof MinimumRemainingEquityValidationError && error.isWarningError()) {
          return error.message;
        }
      }
    }

    return null;
  }, [productType, values]);

  useEffect(() => {
    (async () => {
      setLoanAmountError(await validateMRE());
    })();
  }, [values, validateMRE]);

  useEffect(() => {
    const foundProduct = products.find((product) => {
      const typedProduct = product as Option<{ code: string }>;
      return values.loanDetails.mortgageProduct && typedProduct.params
        ? typedProduct.params.code === values.loanDetails.mortgageProduct.toString()
        : [];
    }) as Option<{ code: string; availableRepaymentTypes: ProductRepaymentType[] }> | undefined;

    if (foundProduct && foundProduct.params) {
      setAvailableRepaymentTypes(mapRepaymentTypes(foundProduct.params.availableRepaymentTypes));
    }

    if (
      foundProduct &&
      foundProduct.params &&
      values.loanDetails.productRepaymentType &&
      !foundProduct.params.availableRepaymentTypes.includes(values.loanDetails.productRepaymentType)
    ) {
      setFieldValue(`${FIELDS.LOAN_DETAILS_PREFIX}.${FIELDS.PRODUCT_REPAYMENT_TYPE}`, null);
    }
  }, [values.loanDetails.mortgageProduct, products, setFieldValue, values.loanDetails.productRepaymentType]);

  return (
    <>
      <Field
        component={Fields.Select}
        title={FIELDS_QUESTIONS.MORTGAGE_PRODUCT}
        name={getName(FIELDS.MORTGAGE_PRODUCT)}
        options={products}
        noOptionsText={'No products'}
        handleClick={handleProductChange}
        required
      />
      {productType && productType !== ProductTypes.RIO && (
        <Field
          component={Fields.Select}
          title={FIELDS_QUESTIONS.PRODUCT_REPAYMENT_TYPE}
          name={getName(FIELDS.PRODUCT_REPAYMENT_TYPE)}
          options={availableRepaymentTypes}
          required
        />
      )}
      {productType === ProductTypes.TIO && (
        <Columns spacing={4}>
          <Field
            component={Fields.Select}
            title={FIELDS_QUESTIONS.TERM_LENGTH}
            name={getName(FIELDS.TERM_LENGTH)}
            options={termLengths}
            required
          />
        </Columns>
      )}
      <Columns spacing={4}>
        <Field
          component={Fields.Text}
          title={FIELDS_QUESTIONS.LOAN_REQUESTED}
          name={getName(FIELDS.LOAN_REQUESTED)}
          variant={VARIANTS_TYPES.CURRENCY}
          extraContent={
            loanAmountError
              ? [
                  {
                    position: 'bottom',
                    variant: 'component',
                    content: <span style={{ color: 'red' }}>{loanAmountError}</span>,
                  },
                ]
              : []
          }
        />
        <></>
      </Columns>
      <Columns spacing={4}>
        <Field
          component={Fields.Text}
          title={FIELDS_QUESTIONS.PROPERTY_VALUE}
          name={getName(FIELDS.PROPERTY_VALUE)}
          variant={VARIANTS_TYPES.CURRENCY}
        />
        <Field
          component={Fields.Text}
          title={FIELDS_QUESTIONS.POST_CODE}
          name={getName(FIELDS.POST_CODE)}
          variant={VARIANTS_TYPES.POSTCODE}
        />
      </Columns>
      <Field
        component={Fields.Select}
        title={FIELDS_QUESTIONS.IS_PRIMARY_HOME}
        name={getName(FIELDS.IS_PRIMARY_HOME)}
        options={yesNoOptions}
        noOptionsText={'Not selected'}
      />
      <Field
        component={Fields.Text}
        title={FIELDS_QUESTIONS.MONTHLY_LIVING_COSTS}
        name={getName(FIELDS.MONTHLY_LIVING_COSTS)}
        variant={VARIANTS_TYPES.CURRENCY}
      />
      <Field
        component={Fields.Text}
        title={FIELDS_QUESTIONS.ANNUAL_COUNCIL_TAX}
        name={getName(FIELDS.ANNUAL_COUNCIL_TAX)}
        variant={VARIANTS_TYPES.CURRENCY}
      />
      <Field
        component={Fields.Text}
        title={FIELDS_QUESTIONS.ANNUAL_GROUND_RENT_SERVICE_CHARGE}
        name={getName(FIELDS.ANNUAL_GROUND_RENT_SERVICE_CHARGE)}
        variant={VARIANTS_TYPES.CURRENCY}
      />
    </>
  );
};

export default LoanDetails;
